import React, { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import { useConfig } from "../../context/ConfigContext"
import AuthModal from "../auth/AuthModal"
import BookingModal from "./BookingModal"
import API from "../../api/axios"
import "./live.css"

const platformLabels = { zoom: "Zoom", google_meet: "Google Meet", microsoft_teams: "MS Teams", other: "Online" }
const platformIcons  = { zoom: "fa-video", google_meet: "fa-globe", microsoft_teams: "fa-globe", other: "fa-link" }

const formatDate = (d) => new Date(d).toLocaleString("en-KE", {
  weekday: "short", day: "numeric", month: "short",
  year: "numeric", hour: "2-digit", minute: "2-digit",
})

const StatusBadge = ({ status }) => {
  const map = {
    live:      { cls: "live-now",  icon: "fa-circle", label: "🔴 Live Now" },
    upcoming:  { cls: "upcoming",  icon: "fa-calendar", label: "📅 Upcoming" },
    completed: { cls: "completed", icon: "fa-check",    label: "✅ Completed" },
    cancelled: { cls: "cancelled", icon: "fa-ban",      label: "Cancelled" },
  }
  const s = map[status] || map.upcoming
  return <span className={`live-status-badge ${s.cls}`}>{s.label}</span>
}

const SessionCard = ({ session, bookedIds, onBook, onRefresh }) => {
  const { isUser }         = useAuth()
  const { paymentEnabled } = useConfig()
  const isBooked           = bookedIds.includes(session.id)
  const isFree             = session.isFree
  const showComingSoon     = !isFree && !isBooked && !paymentEnabled && session.status === "upcoming"
  const [detail, setDetail] = useState(null)

  // Fetch meeting details if booked
  useEffect(() => {
    if (isBooked && isUser) {
      API.get(`/live-sessions/${session.id}`)
        .then(r => setDetail(r.data.data))
        .catch(() => {})
    }
  }, [isBooked, isUser, session.id])

  const meetingData = isBooked ? detail : null

  return (
    <div className="live-card">
      {/* Thumbnail */}
      <div className="live-card-thumb">
        {session.thumbnail
          ? <img src={session.thumbnail} alt={session.title} />
          : <div className="live-card-thumb-placeholder"><i className="fa fa-video"></i></div>
        }
        <StatusBadge status={session.status} />
        <span className="live-platform-badge">
          <i className={`fa ${platformIcons[session.platform] || "fa-video"}`}></i>
          {platformLabels[session.platform] || "Online"}
        </span>
      </div>

      {/* Body */}
      <div className="live-card-body">
        <h3>{session.title}</h3>

        <div className="live-card-details">
          <div className="live-card-detail"><i className="fa fa-calendar"></i><span>{formatDate(session.sessionDate)}</span></div>
          <div className="live-card-detail"><i className="fa fa-clock"></i><span>{session.duration} minutes</span></div>
          <div className="live-card-detail"><i className="fa fa-user-tie"></i><span>{session.instructor || "Mr. Ali"}</span></div>
          <div className="live-card-detail">
            <i className="fa fa-users"></i>
            <span>{session.bookedCount || 0} / {session.maxParticipants} spots</span>
          </div>
        </div>

        {/* Price */}
        <div className="live-card-price">
          {isFree
            ? <span className="live-price-free"><i className="fa fa-unlock"></i> Free</span>
            : <><i className="fa fa-tag"></i> KES {Number(session.price).toLocaleString()}</>
          }
        </div>

        {/* Booked + meeting details */}
        {isBooked && (
          <div className="live-booked-badge">
            <i className="fa fa-check-circle"></i> You're Booked
          </div>
        )}

        {isBooked && meetingData?.meetingUrl && (
          <div className="live-meeting-box">
            <span className="meet-label"><i className="fa fa-link"></i> Meeting Details</span>
            <a href={meetingData.meetingUrl} target="_blank" rel="noreferrer">{meetingData.meetingUrl}</a>
            {meetingData.meetingId && (
              <div className="live-meeting-row"><span>Meeting ID</span><strong>{meetingData.meetingId}</strong></div>
            )}
            {meetingData.meetingPassword && (
              <div className="live-meeting-row"><span>Password</span><strong>{meetingData.meetingPassword}</strong></div>
            )}
          </div>
        )}

        {/* Recording */}
        {session.status === "completed" && isBooked && meetingData?.recordingUrl && (
          <a href={meetingData.recordingUrl} target="_blank" rel="noreferrer" className="live-card-btn recording">
            <i className="fa fa-play-circle"></i> Watch Recording
          </a>
        )}

        {/* CTA buttons */}
        {session.status === "live" && isBooked && meetingData?.meetingUrl && (
          <a href={meetingData.meetingUrl} target="_blank" rel="noreferrer" className="live-card-btn join-now">
            <i className="fa fa-video"></i> Join Now
          </a>
        )}

        {session.status === "upcoming" && !isBooked && (
          showComingSoon ? (
            <button className="live-card-btn ended" disabled>
              <i className="fa fa-clock"></i> Payments Coming Soon
            </button>
          ) : (
            <button className="live-card-btn book" onClick={() => onBook(session)}>
              <i className="fa fa-calendar-plus"></i> Book Session
            </button>
          )
        )}

        {session.status === "upcoming" && isBooked && !meetingData?.meetingUrl && (
          <button className="live-card-btn ended" disabled>
            <i className="fa fa-calendar-check"></i> Spot Reserved
          </button>
        )}

        {session.status === "completed" && !meetingData?.recordingUrl && (
          <button className="live-card-btn ended" disabled>
            <i className="fa fa-history"></i> Session Ended
          </button>
        )}

        {session.status === "cancelled" && (
          <button className="live-card-btn ended" disabled>
            <i className="fa fa-ban"></i> Cancelled
          </button>
        )}
      </div>
    </div>
  )
}

const LiveSessions = () => {
  const { isUser }         = useAuth()
  const { paymentEnabled } = useConfig()
  const navigate           = useNavigate()

  const [sessions,   setSessions]   = useState([])
  const [bookedIds,  setBookedIds]  = useState([])  // session IDs user has booked
  const [loading,    setLoading]    = useState(true)
  const [filter,     setFilter]     = useState("all")
  const [authOpen,   setAuthOpen]   = useState(false)
  const [bookingOpen,setBookingOpen]= useState(false)
  const [selected,   setSelected]   = useState(null)

  const fetchSessions = useCallback(async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : ""
      const { data } = await API.get(`/live-sessions${params}`)
      setSessions(data.data || [])
    } catch { setSessions([]) }
    finally { setLoading(false) }
  }, [filter])

  const fetchMyBookings = useCallback(async () => {
    if (!isUser) return
    try {
      const { data } = await API.get("/live-sessions/my-bookings")
      setBookedIds((data.data || []).map(b => b.sessionId))
    } catch {}
  }, [isUser])

  useEffect(() => { fetchSessions() }, [fetchSessions])
  useEffect(() => { fetchMyBookings() }, [fetchMyBookings])

  const handleBook = (session) => {
    if (!isUser) { setAuthOpen(true); return }
    setSelected(session)
    setBookingOpen(true)
  }

  const handleBooked = () => {
    fetchMyBookings()
    fetchSessions()
  }

  const filters = [
    { key: "all",       label: "All Sessions" },
    { key: "upcoming",  label: "Upcoming" },
    { key: "live",      label: "Live Now" },
    { key: "completed", label: "Completed" },
  ]

  return (
    <>
      {/* Hero */}
      <div className="live-hero">
        <div className="container">
          <div className="live-hero-eyebrow">
            <div className="live-dot"></div>
            Live Trading Sessions
          </div>
          {/* <h1>Trade Live with Mr. Ali</h1> */}
          <p>Join real-time trading sessions — analyse markets, ask questions, and learn by doing.</p>
        </div>
      </div>

      <section className="padding" style={{ background: "var(--bg)" }}>
        <div className="container">

          {/* Filters */}
          <div className="live-filters">
            {filters.map(f => (
              <button
                key={f.key}
                className={`live-filter-btn ${filter === f.key ? "active" : ""}`}
                onClick={() => { setFilter(f.key); setLoading(true) }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="live-grid">
              <div className="live-empty">
                <i className="fa fa-spinner fa-spin"></i>
                <span style={{ color: "var(--text-muted)", fontSize: 14 }}>Loading sessions…</span>
              </div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="live-grid">
              <div className="live-empty">
                <i className="fa fa-calendar-times"></i>
                <h3>No sessions found</h3>
                <p>Check back soon — new live sessions are scheduled regularly.</p>
              </div>
            </div>
          ) : (
            <div className="live-grid">
              {sessions.map(s => (
                <SessionCard
                  key={s.id}
                  session={s}
                  bookedIds={bookedIds}
                  onBook={handleBook}
                  onRefresh={handleBooked}
                />
              ))}
            </div>
          )}

        </div>

        {/* Coming Soon strip for paid sessions */}
        {!paymentEnabled && (
          <div className="coming-soon-strip" style={{ marginTop: 0, borderRadius: "0 0 12px 12px" }}>
            <div className="coming-soon-strip-icon"><i className="fa fa-clock"></i></div>
            <div className="coming-soon-strip-text">
              <strong>Online Payments Coming Soon</strong>
              <span>To book a paid session, contact us via WhatsApp or email.</span>
            </div>
            <div className="coming-soon-strip-actions">
              <a href="https://wa.me/254722525826" target="_blank" rel="noreferrer" className="strip-wa-btn">
                <i className="fab fa-whatsapp"></i> WhatsApp
              </a>
              <a href="mailto:info@rovers.ke" className="strip-email-btn">
                <i className="fa fa-envelope"></i> Email Us
              </a>
            </div>
          </div>
        )}

      </section>

      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} mode="register" />
      <BookingModal
        session={selected}
        isOpen={bookingOpen}
        onClose={() => { setBookingOpen(false); setSelected(null) }}
        onBooked={handleBooked}
      />
    </>
  )
}

export default LiveSessions
